import React, { useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ConditionFeatureFlag,
  ConditionNumericOperator,
  ConditionStatementCustomText,
  ConditionTextOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  getLookupDestinationTableColumn,
  NUMERIC_OPERATOR_WITH_INPUT_RHS,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
} from '@makyoapp/makyo-common'
import { Date16, Numfield16, Textfield } from '@makyoapp/makyo-icon/icon'
import { DateTimeInput } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useDebounceInput } from '@/shared/makyo-hooks'

import { useConditionRepeaterContext } from './ConditionRepeaterContext'
import { useConditionSelectContext } from './ConditionSelectContext'

export function ConditionCustomTextDD() {
  const { tables } = useDbClientContext()
  const { setOpen, isRhs, index, inputRef, rhsInputRef, isEnd } = useConditionSelectContext()
  const { onChange, value } = useConditionRepeaterContext()

  const valKey = isRhs && isEnd ? 'endValue' : 'value'

  const icon = useMemo(() => {
    if (!isRhs) {
      return (
        <>
          <Textfield />
          <div>
            <Trans>Custom Text</Trans>
          </div>
        </>
      )
    }
    const lhsVal = value.conditions[index].lhs
    if (lhsVal.flag !== ConditionFeatureFlag.ColumnData) {
      return (
        <>
          <Textfield />
          <div>
            <Trans>Custom Text</Trans>
          </div>
        </>
      )
    }
    const lhsTbl = tables.find((tb) => tb.id === lhsVal.tbl)
    let lhsCol = lhsTbl?.columns.find((c) => c.id === lhsVal.col)
    if (lhsTbl && lhsCol?.type === ColumnTypes.Lookup) {
      lhsCol = getLookupDestinationTableColumn(lhsCol, lhsTbl, tables).fCol
    }
    if (lhsCol?.type === ColumnTypes.Number) {
      return (
        <>
          <Numfield16 />
          <div>
            <Trans>Custom Number</Trans>
          </div>
        </>
      )
    }

    if (lhsCol?.type === ColumnTypes.DateTime) {
      return (
        <>
          <Date16 />
          <div>
            <Trans>Custom Date</Trans>
          </div>
        </>
      )
    }

    return (
      <>
        <Textfield />
        <div>
          <Trans>Custom Text</Trans>
        </div>
      </>
    )
  }, [isRhs, index, value, tables])

  return (
    <button
      className='gap-2 sdd-item cond-custom-text'
      onClick={async () => {
        const key = isRhs ? 'rhs' : 'lhs'
        const nextCond = [...value.conditions]
        const isSame = !isRhs ? nextCond[index].lhs.flag === ConditionFeatureFlag.CustomText : true

        const nextOp = isSame ? nextCond[index].operator : ConditionTextOperator.IsNotEmpty

        nextCond[index] = {
          ...nextCond[index],
          [key]: {
            flag: ConditionFeatureFlag.CustomText,
            value: (nextCond[index]?.[key] as ConditionStatementCustomText)?.value,
            endValue: (nextCond[index]?.[key] as ConditionStatementCustomText)?.endValue,
            [valKey]: '',
          },
          operator: nextOp,
        }
        onChange?.({
          isAny: value.isAny,
          conditions: nextCond,
        })
        setOpen(false)

        // focus input
        if (isRhs) {
          rhsInputRef.current?.focus()
        } else {
          inputRef.current?.focus()
        }
      }}
    >
      {icon}
    </button>
  )
}

export function ConditionCustomTextStatement({ statement }: { statement: ConditionStatementCustomText }) {
  const { id, value, onChange } = useConditionRepeaterContext()
  const { isRhs, index, rhsInputRef, inputRef, setFocused, focused, isEnd } = useConditionSelectContext()

  const key = isRhs ? 'rhs' : 'lhs'
  const valKey = isRhs && isEnd ? 'endValue' : 'value'
  const currentValue = statement[valKey]

  useEffect(() => {
    const ref = isRhs ? rhsInputRef : inputRef
    if (ref.current && !focused[key]) {
      setFocused({
        [key]: true,
      })
      ref.current.focus()
    }
  }, [inputRef, isRhs, rhsInputRef, key, focused, setFocused])

  if ([...DATE_OPERATOR_WITH_RANGE_RHS, ...DATE_OPERATOR_WITH_RHS].includes(value.conditions?.[index]?.operator)) {
    return (
      <DateTimeInput
        key={`${id}-${index}-rhs-date`}
        value={currentValue || null}
        onChange={(date) => {
          const nextCond = [...value.conditions]
          nextCond[index] = {
            ...nextCond[index],
            [key]: {
              ...nextCond[index][key],
              [valKey]: date,
            } as ConditionStatementCustomText,
          }
          onChange?.({
            isAny: value.isAny,
            conditions: nextCond,
          })
        }}
      />
    )
  }
  return (
    <DebounceInput
      key={`${id}-${index}-${key}`}
      ref={isRhs ? rhsInputRef : inputRef}
      onChange={(next) => {
        const isNumOp = [...NUMERIC_OPERATOR_WITH_INPUT_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(
          value.conditions?.[index]?.operator as ConditionNumericOperator
        )

        const numText = Number(next)
        const isValidNum = !isNaN(numText) || next === '-'

        const nextCond = [...value.conditions]
        nextCond[index] = {
          ...nextCond[index],
          [key]: {
            ...nextCond[index][key],
            [valKey]: !isNumOp ? next : isValidNum ? next : '0',
          } as ConditionStatementCustomText,
        }
        onChange?.({
          isAny: value.isAny,
          conditions: nextCond,
        })
      }}
      value={statement[valKey] || ''}
    />
  )
}

const DebounceInput = React.forwardRef<HTMLInputElement, { value: string; onChange: (next: string) => void }>(
  ({ value, onChange }, ref) => {
    const { t } = useTranslation()

    const [debouncedValue, cancelDebounce, setDebounceValue, currentValue, setCurrentValue] = useDebounceInput(value)

    useEffect(() => {
      onChange(debouncedValue)

      return () => {
        cancelDebounce()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue])

    return (
      <input
        ref={ref}
        className='bg-transparent min-w-0 w-full txt'
        type='text'
        placeholder={t('Enter custom value')}
        onChange={(e) => {
          setCurrentValue(e.currentTarget.value)
        }}
        value={currentValue}
      />
    )
  }
)
DebounceInput.displayName = 'DebounceInput'
